'use client';
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/* LegalAccordion — renders a list of numbered legal sections as expandable
   accordion cards. First card opens by default.

   Each section: { num, title, body (string, may contain \\n\\n paragraph
   breaks and **bold** markers), defs?: [{term, def}] }. */
export default function LegalAccordion({ sections }) {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="legalacc">
      {sections.map((sec, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={sec.num} className={`legalacc__item${isOpen ? ' legalacc__item--open' : ''}`}>
            <button
              type="button"
              className="legalacc__head"
              onClick={() => setOpenIdx(isOpen ? -1 : i)}
              aria-expanded={isOpen}
            >
              <span className="legalacc__num">{String(sec.num).padStart(2, '0')}</span>
              <span className="legalacc__title">{sec.title}</span>
              <span className="legalacc__toggle" aria-hidden="true">
                <ChevronDown size={18} strokeWidth={2.4} />
              </span>
            </button>
            {isOpen && (
              <div className="legalacc__body">
                {sec.body.split(/\n\n/).map((para, p) => (
                  <p key={p} dangerouslySetInnerHTML={{
                    __html: para.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'),
                  }} />
                ))}
                {sec.defs && (
                  <dl className="legalacc__defs">
                    {sec.defs.map((d, k) => (
                      <div key={k} className="legalacc__def">
                        <dt>&ldquo;{d.term}&rdquo;</dt>
                        <dd>{d.def}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}
          </div>
        );
      })}

      <style jsx>{`
        .legalacc {
          display: flex;
          flex-direction: column;
          gap: 12px;
          font-family: var(--font-inter), system-ui, sans-serif;
        }
        .legalacc__item {
          background: #FFFFFF;
          border: 1.5px solid var(--border-light);
          border-radius: 16px;
          overflow: hidden;
          transition: border-color 0.18s, box-shadow 0.18s;
        }
        .legalacc__item--open {
          border-color: var(--brand);
          box-shadow: 0 14px 32px -16px rgba(0, 152, 255, 0.18);
        }
        .legalacc__head {
          width: 100%;
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          gap: 18px;
          padding: 22px 26px;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          font: inherit;
          color: inherit;
        }
        .legalacc__num {
          display: inline-flex; align-items: center; justify-content: center;
          width: 40px; height: 40px;
          border-radius: 10px;
          background: var(--brand-subtle);
          color: var(--brand);
          font-family: var(--font-inter-tight), system-ui, sans-serif;
          font-size: 14px; font-weight: 800;
          letter-spacing: -0.01em;
          flex-shrink: 0;
        }
        .legalacc__item--open .legalacc__num {
          background: var(--brand);
          color: #FFFFFF;
        }
        .legalacc__title {
          font-size: 16px; font-weight: 700;
          color: var(--text-primary);
          letter-spacing: -0.012em;
          line-height: 1.35;
        }
        .legalacc__toggle {
          flex-shrink: 0;
          width: 32px; height: 32px;
          border-radius: 50%;
          background: var(--brand-subtle);
          color: var(--brand);
          display: inline-flex; align-items: center; justify-content: center;
          transition: transform 0.2s, background 0.2s, color 0.2s;
        }
        .legalacc__item--open .legalacc__toggle {
          transform: rotate(180deg);
          background: var(--brand);
          color: #FFFFFF;
        }
        .legalacc__body {
          padding: 6px 26px 26px 84px;
          border-top: 1px solid var(--border-light);
        }
        .legalacc__body :global(p) {
          margin: 16px 0 0;
          font-size: 14.5px;
          color: var(--text-secondary);
          line-height: 1.75;
        }
        .legalacc__body :global(p:first-child) { margin-top: 14px; }
        .legalacc__body :global(a) { color: var(--brand); font-weight: 600; }
        .legalacc__body :global(strong) { color: var(--text-primary); font-weight: 700; }

        .legalacc__defs {
          margin: 18px 0 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .legalacc__def {
          padding: 14px 18px;
          background: #F8FAFF;
          border: 1px solid var(--brand-border);
          border-radius: 12px;
        }
        .legalacc__def dt {
          font-size: 13px;
          font-weight: 800;
          color: var(--brand);
          margin-bottom: 6px;
          letter-spacing: -0.01em;
        }
        .legalacc__def dd {
          margin: 0;
          font-size: 13.5px;
          color: var(--text-secondary);
          line-height: 1.7;
        }

        @media (max-width: 600px) {
          .legalacc__head { padding: 16px 18px; gap: 12px; }
          .legalacc__num { width: 36px; height: 36px; font-size: 13px; }
          .legalacc__title { font-size: 14.5px; }
          .legalacc__body { padding: 4px 18px 20px 18px; }
          .legalacc__body :global(p) { font-size: 14px; }
        }
      `}</style>
    </div>
  );
}
